import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  createLoanProduct,
  getLoanProducts,
  updateLoanProduct,
} from "../../../apis/loanProducts";
import { formatDate } from "../../../environment";
import {
  errorAlert,
  successAlert,
} from "../../../style/scss/utils/Alerts";

type LoanProduct = {
  id: number;
  scheme_name: string;
  loan_amount: number;
  emi_amount: number;
  number_of_days: number;
  number_of_emis: number;
  createdAt?: string;
};

const emptyProduct = {
  scheme_name: "",
  loan_amount: "",
  emi_amount: "",
  number_of_days: "",
  number_of_emis: "",
};

const LoanProducts = () => {
  const [products, setProducts] = useState<LoanProduct[]>([]);
  const [form, setForm] = useState(emptyProduct);
  const [editingProduct, setEditingProduct] = useState<LoanProduct | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const schemaName = sessionStorage.getItem("schemaName") || undefined;
  const user = JSON.parse(sessionStorage.getItem("info") || "{}");
  const canManageProducts = user.type === "financer";
  const requestData = {
    ...(schemaName ? { schemaName } : {}),
    user_id: user.id,
    vendor_id: user.vendor_id,
    user_type: user.type,
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await getLoanProducts(requestData);
      setProducts(response?.data || []);
    } catch (error) {
      errorAlert("Unable to load loan products", "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) return products;
    return products.filter((product) =>
      product.scheme_name.toLowerCase().includes(searchValue)
    );
  }, [products, search]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / rowsPerPage));
  const firstRow = (currentPage - 1) * rowsPerPage;
  const visibleProducts = filteredProducts.slice(firstRow, firstRow + rowsPerPage);

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setForm(emptyProduct);
    setEditingProduct(null);
  };

  const openCreateModal = () => {
    setForm(emptyProduct);
    setEditingProduct(null);
    setShowModal(true);
  };

  const openEditModal = (product: LoanProduct) => {
    setEditingProduct(product);
    setForm({
      scheme_name: product.scheme_name || "",
      loan_amount: String(product.loan_amount || ""),
      emi_amount: String(product.emi_amount || ""),
      number_of_days: String(product.number_of_days),
      number_of_emis: String(product.number_of_emis || ""),
    });
    setShowModal(true);
  };

  const submitProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const loanAmount = Number(form.loan_amount);
    const emiAmount = Number(form.emi_amount);
    const numberOfDays = Number(form.number_of_days);
    const numberOfEmis = Number(form.number_of_emis);

    if (emiAmount * numberOfEmis < loanAmount) {
      errorAlert(
        "Invalid loan product",
        "Total EMI collection must be at least the loan amount."
      );
      return;
    }

    setSaving(true);
    const payload = {
      ...requestData,
      scheme_name: form.scheme_name.trim(),
      loan_amount: loanAmount,
      emi_amount: emiAmount,
      number_of_days: numberOfDays,
      number_of_emis: numberOfEmis,
    };

    try {
      if (editingProduct) {
        await updateLoanProduct(editingProduct.id, payload);
        successAlert("Success", "Loan type updated successfully.");
      } else {
        await createLoanProduct(payload);
        successAlert("Success", "Loan type created successfully.");
      }
      setShowModal(false);
      setForm(emptyProduct);
      setEditingProduct(null);
      await loadProducts();
    } catch (error: any) {
      errorAlert(
        "Unable to save loan type",
        error?.response?.data?.message || "Please check the loan type details."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-1">Loan Types</h4>
              <p className="text-muted mb-0">
                Configure product-based loan amounts and EMI schedules.
              </p>
            </div>
            {canManageProducts && (
              <button className="btn btn-primary" onClick={openCreateModal}>
                <i className="ti ti-circle-plus me-2" />
                Add Loan Type
              </button>
            )}
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
              <h5 className="mb-0">Loan Type List</h5>
              <div className="input-icon-start position-relative" style={{ minWidth: 260 }}>
                <span className="input-icon-addon">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search loan type"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <div className="card-body p-0 table-responsive">
              <table className="table table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Sr No.</th>
                    <th>Date</th>
                    <th>Scheme Name</th>
                    <th>Loan Amount</th>
                    <th>EMI Amount</th>
                    <th>EMI Every After Day</th>
                    <th>Number of EMIs</th>
                    {canManageProducts && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={canManageProducts ? 8 : 7} className="text-center py-4">
                        Loading loan types...
                      </td>
                    </tr>
                  ) : visibleProducts.length === 0 ? (
                    <tr>
                      <td colSpan={canManageProducts ? 8 : 7} className="text-center py-4">
                        No loan types found.
                      </td>
                    </tr>
                  ) : (
                    visibleProducts.map((product, index) => (
                      <tr key={product.id}>
                        <td>{firstRow + index + 1}</td>
                        <td>{formatDate(product.createdAt)}</td>
                        <td className="fw-medium">{product.scheme_name}</td>
                        <td>{product.loan_amount}</td>
                        <td>{product.emi_amount}</td>
                        <td>
                          {Number(product.number_of_days) === 0
                            ? "Monthly"
                            : `${product.number_of_days} days`}
                        </td>
                        <td>{product.number_of_emis}</td>
                        {canManageProducts && (
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-icon btn-primary"
                              onClick={() => openEditModal(product)}
                              aria-label={`Edit ${product.scheme_name}`}
                            >
                              <i className="ti ti-edit" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Rows per page</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 80 }}
                  value={rowsPerPage}
                  onChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 20, 50, 100].map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-light"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal fade show d-block" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingProduct ? "Edit Loan Type" : "Add Loan Type"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeModal}
                  />
                </div>
                <form onSubmit={submitProduct}>
                  <div className="modal-body">
                    <div className="mb-4 border-bottom pb-2">
                      <h6 className="text-primary mb-0">Basic Information</h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Scheme Name <span className="text-danger">*</span></label>
                        <input
                          required
                          className="form-control"
                          placeholder="Enter scheme name"
                          value={form.scheme_name}
                          onChange={(event) => setForm({ ...form, scheme_name: event.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Loan Amount <span className="text-danger">*</span></label>
                        <input
                          required
                          min="1"
                          type="number"
                          className="form-control"
                          placeholder="Enter loan amount"
                          value={form.loan_amount}
                          onChange={(event) => setForm({ ...form, loan_amount: event.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">EMI Amount <span className="text-danger">*</span></label>
                        <input
                          required
                          min="1"
                          type="number"
                          className="form-control"
                          placeholder="Enter EMI amount"
                          value={form.emi_amount}
                          onChange={(event) => setForm({ ...form, emi_amount: event.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">EMI Every After Day <span className="text-danger">*</span></label>
                        <input
                          required
                          min="0"
                          type="number"
                          className="form-control"
                          placeholder="Enter number of days"
                          value={form.number_of_days}
                          onChange={(event) => setForm({ ...form, number_of_days: event.target.value })}
                        />
                        <small className="text-muted">Enter 0 for monthly EMI generation.</small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Number of EMIs <span className="text-danger">*</span></label>
                        <input
                          required
                          min="1"
                          type="number"
                          className="form-control"
                          placeholder="Enter number of EMIs"
                          value={form.number_of_emis}
                          onChange={(event) => setForm({ ...form, number_of_emis: event.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : editingProduct ? "Update" : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

export default LoanProducts;
